import React, { useMemo, useRef, useState } from "react";
import { useIntl } from "umi";
import classNames from 'classnames';
import { Dropdown, Segment } from "tea-component";
import ColorSvg from "../CustomColorSelector/ColorSvg";
import ColorPalette from "../ColorPalette";
import GradientDropdown from "../GradientDropdown";
import "./index.scss";

interface IColorDropdownProps {
  className?: string;
  boxClassName?: string;
  options?: any[];
  colorTheme: {
    type: string;
    id?: string;
    color: {
      hexa: string;
      alpha: string;
    }[];
  };
  onChange: Function;
}

const ColorDropdown: React.FC<IColorDropdownProps> = (props) => {
  const { formatMessage } = useIntl();
  const { className, boxClassName, colorTheme, onChange, ...dropdownProps } = props;
  const colorPaletteRef = useRef(null);
  const gradientDropdownRef = useRef(null);
  const { color, type, id } = colorTheme || {};
  const [segmentValue, setSegmentValue] = useState<string>(type || 'solidColor');
  const options = props?.options || [
    {
      value: 'solidColor',
      text: formatMessage({ id: 'dbl_chart_condition_solid_color' }),
    },
    {
      value: 'gradientColor',
      text: formatMessage({ id: 'dbl_chart_condition_gradient' }),
    }
  ];

  const handleDropdownClose = () => {
    try {
      if (segmentValue === 'solidColor' && colorPaletteRef?.current?.colorInfo) {
        setTimeout(() => {
          onChange?.({ type: 'solidColor', color: [colorPaletteRef.current.colorInfo] });
        }, 50);
      } else if (segmentValue === 'gradientColor' && gradientDropdownRef?.current?.colorTheme) {
        setTimeout(() => {
          onChange?.(gradientDropdownRef?.current?.colorTheme);
        }, 50);
      }
    } catch (error) {
      onChange?.(colorTheme);
    }
  };

  const button = useMemo(() => {
    if (type === 'gradientColor') {
      return <div className="dbl-color-dropdown-button">
        <div className="dbl-color-dropdown-button-text">
          {formatMessage({ id: 'dbl_chart_condition_gradient' })}
        </div>
        <div className="dbl-color-dropdown-button-value">
          {
            color?.map((item, index) => <ColorSvg key={index} color={item.hexa} />)
          }
        </div>
      </div>;
    }
    return <div className="dbl-color-dropdown-button">
      <div className="dbl-color-dropdown-button-text">
        {formatMessage({ id: 'dbl_chart_condition_solid_color' })}
      </div>
      <div className="dbl-color-dropdown-button-value">
        <svg width="108" height="20" viewBox="0 0 108 20">
          <rect x="0" y="0" width="108" height="20" rx="2" fill={color?.[0]?.hexa} />
        </svg>
      </div>
    </div>;
  }, [color, formatMessage, type]);

  const colorInfo = useMemo(() => {
    if (id) return { hexa: "#E0EAFD", alpha: "100" };
    return color?.[0];
  }, [color, id]);

  return (
    <Dropdown
      clickClose={false}
      button={button}
      appearance="button"
      placement="bottom"
      className={classNames('dbl-color-dropdown', className)}
      boxClassName={classNames('dbl-color-dropdown-box', boxClassName)}
      onClose={handleDropdownClose}
      {...(dropdownProps || {})}
    >
      {
        close => <div className={classNames("dbl-color-dropdown-content", `dbl-color-dropdown-content-${segmentValue}`)}>
          <Segment
            value={segmentValue}
            options={options}
            onChange={(value) => {
              setSegmentValue(value);
            }}
          />
          <ColorPalette
            ref={colorPaletteRef}
            className={'dbl-color-dropdown-solid'}
            colorInfo={colorInfo}
            isClose={true}
            onChangeColorInfo={() => {
              close?.();
            }}
          />
          <GradientDropdown
            ref={gradientDropdownRef}
            className={'dbl-color-dropdown-gradient'}
            colorTheme={colorTheme}
            onChange={(config: any) => {
              if (config?.id !== 'custom') {
                close?.();
              }
            }}
          />
        </div>
      }
    </Dropdown>
  );
};

export default ColorDropdown;
